import { useField } from 'formik';
import { useMemo, useState } from 'react';

import { Chip, ChipProps, TextFieldProps ,
  Autocomplete as MuiAutocomplete, AutocompleteGetTagProps, AutocompleteProps,
  AutocompleteRenderInputParams, AutocompleteRenderOptionState,
  MenuItem
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { BaseTextField } from '../BaseTextField';

// currying to render tags with easy chip config and consistency accross codebase (used externally when using the AutocompleteMulti component renderTags)
export const getRenderTagsForAutocompleteMulti = <T extends { id: string }>(
  getLabel: (opt: T) => string,
  chipProps?: ChipProps
) => (
  values: T[],
  getTagProps: AutocompleteGetTagProps
) => {
  return values?.map((value, index) => {
    const label = getLabel(value);

    return <Chip label={label} color="default" variant="outlined" {...chipProps} {...getTagProps({ index })} key={label} />;
  });
};

const useStyles = makeStyles()({
  inputRoot: {
    '&[class*="MuiOutlinedInput-root"]': {
      padding: '5px',
    },
  },
  input: {
    padding: '9px 4px !important',
  }
});

export type AutocompleteMultiProps<
  T,
  TMultiple extends boolean | undefined = true,
  TDisableClearable extends boolean | undefined = undefined,
  TFreeSolo extends boolean | undefined = undefined
> = {
  name: string;
  label?: React.ReactNode;
  helperText?: string;
  extractId?: true;
  options: T[];
  getOptionLabel: (value: T) => string;
  renderTags?: (value: T[], getTagProps: AutocompleteGetTagProps) => React.ReactNode;
  placeholder?: string;
  noOptionsText?: string;
  classes?: AutocompleteProps<T, TMultiple, TDisableClearable, TFreeSolo>['classes'];
  color?: TextFieldProps['color'];

  processValueChange?: (oldValues: T[], newValues: T[]) => T[];
  size?: AutocompleteProps<T, TMultiple, TDisableClearable, TFreeSolo>['size'];
  renderOption?: (v: T, state: AutocompleteRenderOptionState) => React.ReactNode;
  // s?: AutocompleteProps<T, TMultiple, TDisableClearable, TFreeSolo>['renderOption'];
  disableCloseOnSelect?: AutocompleteProps<T, TMultiple, TDisableClearable, TFreeSolo>['disableCloseOnSelect'];
  getOptionDisabled?: AutocompleteProps<T, TMultiple, TDisableClearable, TFreeSolo>['getOptionDisabled'];
  groupBy?: AutocompleteProps<T, TMultiple, TDisableClearable, TFreeSolo>['groupBy'];
  fullWidth?: AutocompleteProps<T, TMultiple, TDisableClearable, TFreeSolo>['fullWidth'];
  disabled?: AutocompleteProps<T, TMultiple, TDisableClearable, TFreeSolo>['disabled'];
  className?: string;
  limitTags?: number;
  autoComplete?: string;
};

export const AutocompleteMulti = <
  T extends { id: string },
  TMultiple extends boolean | undefined = undefined,
  TDisableClearable extends boolean | undefined = undefined,
  TFreeSolo extends boolean | undefined = undefined
>({
  name,
  label,
  options,
  helperText,
  renderOption,
  getOptionLabel,
  extractId,
  renderTags,
  placeholder,
  noOptionsText,
  classes: _classes,
  color,
  processValueChange = (o, n) => n,
  className,
  autoComplete = 'none',
  ...props
}: AutocompleteMultiProps<T, TMultiple, TDisableClearable, TFreeSolo>) => {
  const [ inputValue, setInputValue ] = useState<string>('');
  const { classes: multiAutocompleteClasses } = useStyles();
  // const { classes: autocompleteClasses } = useAutocompleteClasses({ inputValue, classes });
  const [ field, meta, { setValue } ] = useField(name);

  const valueArray = useMemo(() => {
    if (extractId) {
      return field.value?.map?.((id: string) => options.find((opt) => opt?.id === id)) ?? [];
    }
    return field.value ?? [];
  },[ field.value, extractId, options ]) as T[];

  const handleValueChange = (_e: React.ChangeEvent<unknown>, _newValueArray: null | T[]) => {
    const newValueArray = processValueChange(valueArray, _newValueArray || []);
    const val = extractId
      ? newValueArray?.map((ele) => ele.id) ?? []
      : newValueArray;

    setValue(val, true);
  };

  const defaultRenderInput = (params: AutocompleteRenderInputParams) => {
    const _error = Boolean(meta.touched && meta.error);
    const _helperText = _error ? meta.error || helperText : helperText;

    return (
      <BaseTextField
        name={name}
        label={label}
        error={_error}
        helperText={_helperText}
        // className={autocompleteClasses.inputRoot}
        placeholder={!valueArray.length ? placeholder : undefined}
        color={color}
        {...params}
        inputProps={{
          ...params.inputProps,
          autoComplete: autoComplete,
        }}
      />
    );
  };

  const compareValues = (option: T) => {
    if (!valueArray.length) return false;
    return valueArray.findIndex((value) => value?.id === option?.id) !== -1;
  };

  return (
    <MuiAutocomplete
      multiple
      classes={multiAutocompleteClasses}
      onChange={handleValueChange}
      inputValue={inputValue}
      onInputChange={(_e, val) => setInputValue(val)}
      value={valueArray as any[]}
      options={options}
      renderInput={defaultRenderInput}
      getOptionLabel={getOptionLabel}
      getOptionKey={(opt) => opt?.id}
      getOptionDisabled={compareValues}
      renderTags={renderTags}
      noOptionsText={noOptionsText}
      onBlur={field.onBlur}
      className={className}
      {...props}
      renderOption={renderOption ? (props, option, opts) => <MenuItem color="secondary" {...props}>{renderOption?.(option, opts)}</MenuItem> : undefined}
    />
  );
};
